import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { FilePreview } from "@/components/ui/FilePreview";
import { Button } from "@/components/ui/Button";
import { Modal as UIModal } from "@/components/ui/Modal";
import type { FieldRejectionData } from "./Shared";

export interface FieldReasonModalProps {
  visible: boolean;
  onDismiss: () => void;
  onEdit: () => void;
  onUndoDecision: () => void;
  data?: FieldRejectionData;
  applicationId: string;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
}

export function FieldReasonModal({
  visible,
  onDismiss,
  onEdit,
  onUndoDecision,
  data,
  applicationId,
  t,
}: FieldReasonModalProps) {
  return (
    <UIModal
      visible={visible}
      onDismiss={onDismiss}
      title="review.review.fieldState.reasonModal.title"
      closeOnBackdrop
      useScrollView
      footer={
        <View className="flex-row items-center justify-end gap-4">
          <Button
            label="review.review.fieldState.reasonModal.edit"
            variant="tertiary"
            onPress={onEdit}
          />
          <Button
            label="review.review.fieldState.undoDecision"
            variant="destructive"
            onPress={onUndoDecision}
          />
        </View>
      }
    >
      {/* REJECTED FIELD banner — shows field label, value, and optional image */}
      {(data?.fieldLabel || data?.fieldValue || data?.fieldFileKey) && (
        <View className="bg-error-light border border-error-border rounded-lg px-6 py-5 mb-6 gap-2">
          <AppText weight="semibold" className="text-sm text-error tracking-[0.56px]">
            {t("review.review.decision.rejectFieldModal.rejectedFieldLabel")}
          </AppText>
          {data?.fieldLabel && (
            <AppText className="text-sm text-supporting-text-40">{data.fieldLabel}</AppText>
          )}
          {data?.fieldValue && (
            <AppText weight="medium" className="text-base text-dark-text-50">
              {data.fieldValue}
            </AppText>
          )}
          {data?.fieldFileKey && (
            <View className="w-[200px] h-[100px] rounded-xl overflow-hidden bg-stroke-20 mt-1">
              <FilePreview fileKey={data.fieldFileKey} applicationId={applicationId} />
            </View>
          )}
        </View>
      )}

      {/* REASON */}
      <View className="gap-2 py-4">
        <AppText className="text-xs font-semibold text-supporting-text-40 tracking-widest uppercase">
          {t("review.review.fieldState.reasonModal.reason")}
        </AppText>
        <AppText weight="medium" className="text-dark-text-50">
          {data?.reason ?? "—"}
        </AppText>
      </View>

      {/* Divider */}
      <View className="h-px bg-stroke-20" />

      {/* INTERNAL NOTE */}
      <View className="gap-2 py-4">
        <AppText className="text-xs font-semibold text-supporting-text-40 tracking-widest uppercase">
          {t("review.review.fieldState.reasonModal.internalNote")}
        </AppText>
        <AppText weight="medium" className="text-dark-text-50">
          {data?.note ?? "—"}
        </AppText>
      </View>
    </UIModal>
  );
}
